import axios from 'axios';

import { DELEGATE_SUPPORT_ROLE, getDelegation, type IDelegation, scopeRoles } from '@/authz/delegation';
import { APPUSERROLES, getUserData, VESRAPI } from '@/constants/global';

export type VesrAuthRole =
	| 'SuperAdmin'
	| 'SystemAdmin'
	| 'INECHQAdmin'
	| 'StateRECCoordinator'
	| 'FieldOfficer'
	| 'Observer'
	| 'SecurityAgency';

const VESR_TOKEN_KEY = 'vesrAuthToken';
const VESR_TOKEN_EXPIRY_KEY = 'vesrAuthTokenExpiresAt';
const CLOCK_SKEW_BUFFER_S = 60;

/**
 * The JWT `role` claim is a bare string when the user holds ONE role and an array when
 * they hold several — hence `role: string | string[]` on `IAuthState`. Normalize here so
 * a single-role user doesn't hit `.some is not a function`; casting (as the `roleQuery`
 * helpers do) only silences the type error and leaves the runtime crash in place.
 */
export function mapCoreRoleToVesrRole(coreRoles: string | string[], delegation: IDelegation = getDelegation()): VesrAuthRole {
	const roles = Array.isArray(coreRoles) ? coreRoles : [coreRoles];
	// A team member maps by the owner's role, mirroring api.vesr: Candidate / Party Admin owners
	// work as SystemAdmin, an Observer Admin's staff as Observer. Their own `action` claims still
	// gate every console control, and api.vesr enforces them on every call.
	if (roles.includes(DELEGATE_SUPPORT_ROLE)) {
		const owner = delegation.isTeamMember ? delegation.ownerRole : null;
		return owner === APPUSERROLES.CANDIDATE || owner === APPUSERROLES.PARTYADMIN ? 'SystemAdmin' : 'Observer';
	}
	const adminRoles = [APPUSERROLES.SUPERADMIN, APPUSERROLES.PARTYADMIN, APPUSERROLES.CANDIDATE];
	if (roles.some((r) => adminRoles.includes(r))) return 'SystemAdmin';
	return 'Observer';
}

/**
 * An observer organisation's account (Observer Admin, or a field Observer).
 *
 * <p>These must never use the exchange. The token it mints carries only a role and a name: no
 * UserId and no ObserverId. api.vesr reads a token without a core UserId as a native VESR token,
 * whose incident audience is EVERYTHING — so an Observer Admin holding one would see every party's
 * reports instead of their own organisation's. The core JWT carries the organisation, and api.vesr
 * scopes them by it.</p>
 */
export function isObserverAccount(): boolean {
	const raw = getUserData()?.user?.role ?? [];
	// An Observer Admin's team member carries the owner's ObserverId, so it follows the same rule.
	const roles = scopeRoles(Array.isArray(raw) ? raw : [raw]);
	return roles.includes(APPUSERROLES.OBSERVERADMIN) || roles.includes('Observer');
}

/**
 * Accounts that talk to api.vesr with the core JWT only and must never hold a VESR-minted token.
 *
 * Observer organisations (above), and every team member ("Delegate Support"): api.vesr answers
 * their `POST /auth/token` with 403 `team_access_denied`, because a minted token would drop the
 * delegation claims (ActingForUid, OwnerRole, SupportNo, `action`) that scope and grant-filter
 * them. The exchange is skipped on purpose rather than left to fail into the fallback.
 */
export function usesCoreJwtOnly(): boolean {
	return getDelegation().isTeamMember || isObserverAccount();
}

export function getVesrToken(): string | null {
	return localStorage.getItem(VESR_TOKEN_KEY);
}

export function isVesrTokenValid(): boolean {
	// A cached exchanged token (from an earlier session in this browser) must not be used either.
	if (usesCoreJwtOnly()) return false;
	const token = getVesrToken();
	const expiresAt = localStorage.getItem(VESR_TOKEN_EXPIRY_KEY);
	if (!token || !expiresAt) return false;
	const nowS = Math.floor(Date.now() / 1000);
	return nowS < Number(expiresAt) - CLOCK_SKEW_BUFFER_S;
}

let pendingRequest: Promise<boolean> | null = null;

export async function acquireVesrToken(): Promise<boolean> {
	if (pendingRequest) return pendingRequest;

	pendingRequest = (async () => {
		try {
			if (usesCoreJwtOnly()) return false;
			const userData = getUserData();
			if (!userData?.token || !userData.user) return false;

			const rawRoles = userData.user.role ?? [];
			const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
			const role = mapCoreRoleToVesrRole(roles);
			const name = userData.user.FullName || '';
			const email = userData.user.Email || '';

			const { data } = await axios.post(
				`${VESRAPI}/api/v1/vesr/auth/token`,
				{ role, name, email },
				{ headers: { Authorization: `Bearer ${userData.token}` }, timeout: 10_000 }
			);

			if (data?.token) {
				localStorage.setItem(VESR_TOKEN_KEY, data.token);
				if (data.expiresAt) {
					const expiresAtS =
						typeof data.expiresAt === 'number' ? data.expiresAt : Math.floor(new Date(data.expiresAt).getTime() / 1000);
					localStorage.setItem(VESR_TOKEN_EXPIRY_KEY, String(expiresAtS));
				}
				return true;
			}
			return false;
		} catch (_err) {
			// Fire-and-forget: VESR token acquisition is non-critical.
			// VesrLayout safety net will retry on next mount.
			return false;
		} finally {
			pendingRequest = null;
		}
	})();

	return pendingRequest;
}

/**
 * Whether we can talk to api.vesr at all — either a VESR-issued token, or the
 * core JWT it now accepts.
 *
 * The layout gates rendering on this. Gating on the VESR token alone meant a
 * failed exchange blocked the whole Situation Room, which is precisely what made
 * the anonymous token minter load-bearing.
 */
export function hasUsableVesrAuth(): boolean {
	return isVesrTokenValid() || !!getUserData()?.token;
}

export function getVesrTokenSync(): string {
	// Prefer a VESR-issued token when we hold one.
	if (isVesrTokenValid()) return getVesrToken()!;

	// Otherwise fall back to the core JWT.
	//
	// This used to return '' on the grounds that "api.vesr always rejects the
	// core JWT with 401". That was true, and the cause was narrower than it
	// looked: the signing key and audience always matched, but api.vesr pinned a
	// single ValidIssuer while api.core issues a different `iss` per environment
	// (localhost, the ngrok gateway, api.ielect.app). Fixed backend-side, so a
	// core JWT now authenticates and normalizes to a VESR role and a per-user id.
	//
	// This is what lets the Situation Room keep working when the anonymous
	// /auth/token minter is disabled — that endpoint is unauthenticated and can
	// mint SuperAdmin tokens, and this console depending on it was the only
	// reason it had to stay switched on.
	return getUserData()?.token || '';
}
